import {formatMolecularFormula} from 'chemistry/molecules/MoleculeUtil';
import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import type {StoichiometryCustomEvalOptions} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {
  GetChemicalPropertiesFromSmilesQuery as GetChemicalPropertiesFromSmilesQueryResponse,
  GetChemicalPropertiesFromSmilesQueryVariables,
} from 'notebook/structured-tables/molecules/__generated/GetChemicalPropertiesFromSmilesQuery';
import {GET_CHEMICAL_PROPERTIES_FROM_SMILES_QUERY} from 'notebook/structured-tables/molecules/ChemistryQueries';
import {CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import {
  CustomResolutionQueryType,
  DraftChemicalProperty,
  GetChemicalPropertyFromSmilesQuery,
  ServerResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

interface ResolvedChemicalProperties {
  molecularWeight: number | null;
  formulaWeight: number | null;
  chemicalFormula: string | null;
}

const EMPTY_PROPERTIES: ResolvedChemicalProperties = {
  molecularWeight: null,
  formulaWeight: null,
  chemicalFormula: null,
};

function propertiesFromResponse(
  response: GetChemicalPropertiesFromSmilesQueryResponse
): ResolvedChemicalProperties {
  const {chemicalPropertiesFromSmiles} = response;
  if (chemicalPropertiesFromSmiles.__typename !== 'ChemicalPropertiesFromSmilesNode') {
    return EMPTY_PROPERTIES;
  }
  const {molecularWeight, formulaWeight, chemicalFormula} = chemicalPropertiesFromSmiles;
  return {
    molecularWeight: molecularWeight.__typename === 'FloatPropertyNode' ? molecularWeight.value : null,
    formulaWeight: formulaWeight.__typename === 'FloatPropertyNode' ? formulaWeight.value : null,
    chemicalFormula:
      chemicalFormula.__typename === 'ChemicalFormulaNode'
        ? formatMolecularFormula(chemicalFormula.value)
        : null,
  };
}

function resultForProperty(
  property: DraftChemicalProperty,
  properties: ResolvedChemicalProperties
): ServerResolutionResult {
  switch (property) {
    case DraftChemicalProperty.MOLECULAR_WEIGHT:
    case DraftChemicalProperty.FORMULA_WEIGHT: {
      const value =
        property === DraftChemicalProperty.MOLECULAR_WEIGHT
          ? properties.molecularWeight
          : properties.formulaWeight;
      if (value == null) {
        // Property unavailable (unparseable structure or service error) — leave the cell empty
        // with no error so the UI degrades gracefully.
        return makeCustomResolutionResultFromTypedValue(null);
      }
      return makeCustomResolutionResultFromTypedValue({
        type: FieldType.FLOAT,
        value: String(value),
        rawValue: String(value),
        floatValue: value,
      });
    }
    case DraftChemicalProperty.CHEMICAL_FORMULA: {
      const {chemicalFormula} = properties;
      if (chemicalFormula == null) {
        return makeCustomResolutionResultFromTypedValue(null);
      }
      return makeCustomResolutionResultFromTypedValue({
        type: FieldType.TEXT,
        value: chemicalFormula,
      });
    }
    default:
      return makeCustomResolutionResultFromTypedValue(null);
  }
}

/**
 * Resolve molecular weight, formula weight, and chemical formula for draft (unregistered) compounds
 * in the Reaction table, given the row's canonical SMILES. The backend `chemicalPropertiesFromSmiles`
 * field is keyed by a single SMILES string, so fire one query per unique SMILES (Apollo's batch HTTP
 * link coalesces them and the ChemicalPropertyDataLoader dedupes server-side). Each query maps to a
 * single column via its `property`. Uses `allSettled` so a failed query for one SMILES leaves that
 * SMILES unresolved (treated as "no value available" — empty cell, no error) without blocking others.
 */
export async function bulkResolveGetChemicalPropertiesFromSmilesToServerResolutionResult(
  queries: Array<GetChemicalPropertyFromSmilesQuery>
): Promise<Array<ServerResolutionResult>> {
  const uniqueSmiles = [...new Set(queries.map((query) => query.canonicalSmiles))];
  const propertiesBySmiles = new Map<string, ResolvedChemicalProperties>();
  const settled = await Promise.allSettled(
    uniqueSmiles.map((smiles) =>
      doQueryWithoutCache<
        GetChemicalPropertiesFromSmilesQueryResponse,
        GetChemicalPropertiesFromSmilesQueryVariables
      >({query: GET_CHEMICAL_PROPERTIES_FROM_SMILES_QUERY}, {variables: {smiles}})
    )
  );
  settled.forEach((result, index) => {
    if (result.status !== 'fulfilled') {
      return;
    }
    propertiesBySmiles.set(uniqueSmiles[index], propertiesFromResponse(result.value));
  });

  return queries.map((query) => {
    const properties = propertiesBySmiles.get(query.canonicalSmiles) ?? EMPTY_PROPERTIES;
    return resultForProperty(query.property, properties);
  });
}

/**
 * Shared draft-molecule branch for the MW / formula weight / chemical formula column evals.
 *
 * When a Reaction table row's compound has not resolved to a registered molecule, the row carries
 * only a `canonicalSmiles` in its metadata (see `StoichiometryStructuredTableRowMetadata`). In that
 * case resolve the requested property directly from the SMILES via the internal chemistry service.
 * When there is no SMILES, return the original value unchanged (empty cell, no error).
 */
export function resolveDraftPropertyFromSmiles(
  typedValue: TypedValue,
  customEvalOptions: StoichiometryCustomEvalOptions,
  property: DraftChemicalProperty,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  const canonicalSmiles = customEvalOptions?.rowMetadata?.canonicalSmiles;
  if (canonicalSmiles == null || canonicalSmiles.length === 0) {
    return {val: typedValue, error: null, locked: false};
  }
  const {resolvedValue, error} = resolveFromResolutionQuery(
    typedValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.STOICHIOMETRY_TABLE_GET_PROPERTY_FROM_SMILES,
      canonicalSmiles,
      property,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error, locked: false};
}
